// Enveloppe de l'application connectée : en-tête, identité, déconnexion.
//
// Extraite du portefeuille pour que TOUS les écrans la partagent — sinon chaque
// nouvel écran reconstruit son en-tête, et ils divergent au troisième.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLogout, useMaintenance } from '../api/queries';
import '../maintenance/maintenance.css';
import type { User } from '../api/types';
import { Link, useRouter } from '../router';
import { Button, Avatar } from './ui';
import { LogoMark, Wordmark } from './Logo';
import { LegalFooter } from '../legal/LegalPages';
import { FeedbackButton } from '../feedback/FeedbackButton';
import { DemoBadge } from '../demo/DemoUsage';
import { useTheme } from '../theme/theme';
import { DeviceSettingPicker } from './DeviceSetting';
import '../account/account.css';

export function initialsOf(value: string): string {
  const parts = value.split(/[\s.@_-]+/).filter(Boolean).slice(0, 2);
  const letters = parts.map((p) => p[0]?.toUpperCase() ?? '').join('');
  return letters || '?';
}

/** Bascule clair / sombre. L'icône montre le thème VERS LEQUEL on va. */
function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const label = theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre';
  return (
    <button type="button" className="theme-toggle" onClick={toggle} aria-label={label} title={label}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {theme === 'dark' ? (
          <>
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
          </>
        ) : (
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        )}
      </svg>
    </button>
  );
}

/**
 * Menu du compte sur TÉLÉPHONE : l'en-tête n'a pas la place pour cinq liens.
 * L'avatar l'ouvre ; on y retrouve les mêmes actions qu'à l'ordinateur.
 */
function MobileAccountMenu({ user, superAdmin }: { user: User; superAdmin: boolean }) {
  const logout = useLogout();
  const { theme, toggle } = useTheme();
  const { path } = useRouter();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  // Un lien suivi referme le menu ; un toucher à côté aussi.
  useEffect(() => setOpen(false), [path]);
  useEffect(() => {
    if (!open) {
      setConfirming(false);
      return;
    }
    const away = (e: PointerEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <div className="mobile-account" ref={box}>
      <DemoBadge />
      <button
        type="button"
        className="mobile-account-btn"
        aria-expanded={open}
        aria-label="Menu du compte"
        onClick={() => setOpen((v) => !v)}
      >
        <Avatar initials={initialsOf(user.name || user.email)} seed={user.id} size="m" />
      </button>
      {open ? (
        <div className="mobile-account-menu">
          <p className="mobile-account-who">
            <strong>{user.name}</strong>
            <span>{user.email}</span>
          </p>
          <Link to="/compte" className="mobile-account-item">
            Compte
          </Link>
          {superAdmin ? (
            <Link to="/backoffice" className="mobile-account-item">
              Back-office
            </Link>
          ) : null}
          <button type="button" className="mobile-account-item" onClick={toggle}>
            {theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
          </button>
          <DeviceSettingPicker />
          {confirming ? (
            <span className="logout-confirm">
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                Annuler
              </Button>
              <Button variant="primary" onClick={() => logout.mutate()} loading={logout.isPending}>
                Confirmer
              </Button>
            </span>
          ) : (
            <Button variant="ghost" onClick={() => setConfirming(true)}>
              Se déconnecter
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function AppShell({ user, superAdmin = false, children }: { user: User; superAdmin?: boolean; children: ReactNode }) {
  const logout = useLogout();
  const maintenance = useMaintenance();
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="app-shell">
      {/* Pour ceux qui gardent l'accès pendant une maintenance : ne pas oublier
          de l'éteindre. */}
      {maintenance.data?.active ? (
        <div className="mt-banner" role="status">
          Mode maintenance actif — les autres comptes voient la page de maintenance.
          {superAdmin ? (
            <>
              {' '}
              <Link to="/backoffice/maintenance">Gérer</Link>
            </>
          ) : null}
        </div>
      ) : null}
      <header className="app-header">
        <Link to="/" className="app-brand">
          <LogoMark />
          <Wordmark className="app-brand-name" />
          {/* Pendant la bêta, on le dit à côté du nom : ce n'est pas un détail. */}
          <span className="beta-badge t-mono">Bêta</span>
        </Link>

        <div className="app-user">
          {/* Compte ouvert depuis /demo : ce qu'il comprend, d'un survol. */}
          <DemoBadge />
          <Link to="/compte" className="app-header-link">
            Compte
          </Link>
          {/* Visible du seul exploitant ; le serveur revérifie à chaque appel. */}
          {superAdmin ? (
            <Link to="/backoffice" className="app-header-link">
              Back-office
            </Link>
          ) : null}
          <ThemeToggle />
          <Avatar initials={initialsOf(user.name || user.email)} seed={user.id} size="m" />
          <span className="app-user-name">
            {user.name}
            {user.orgRole === 'admin' ? <span className="app-role">Admin</span> : null}
          </span>
          {confirming ? (
            <span className="logout-confirm">
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                Annuler
              </Button>
              {/* `useLogout` purge le cache et pose l'absence de session dans
                  `onSettled` : l'écran de connexion réapparaît même si l'appel
                  réseau a échoué. */}
              <Button variant="primary" onClick={() => logout.mutate()} loading={logout.isPending}>
                Confirmer
              </Button>
            </span>
          ) : (
            <Button variant="ghost" onClick={() => setConfirming(true)}>
              Se déconnecter
            </Button>
          )}
        </div>
        <MobileAccountMenu user={user} superAdmin={superAdmin} />
      </header>
      {children}
      <LegalFooter />
      {/* Toujours là, en bas à droite : dire ce qu'on comprend ou pas (060). */}
      <FeedbackButton />
    </div>
  );
}
